import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type {
  MailCopyTurn, MailDesign, MailDesignsResponse, MailPreview, MailTemplate, MailWarning,
} from '../api/types'
import { useConfirm } from '../components/Confirm'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { PeoplePicker } from '../components/PeoplePicker'
import { ErrorState, Loading } from '../components/States'
import { useToast } from '../components/Toast'
import { shortDate } from '../lib/format'
import { useAsync } from '../lib/useAsync'

// Designing a piece of mail: pick a layout, write the words, see it as it will be
// printed, send it. One panel to write in and one to look at — the words and the
// result, with nothing in between.

const LETTER_PX = { w: 816, h: 1056 }     // 8.5x11in at 96dpi
const PAGE_PX: Record<string, { w: number; h: number }> = {
  letter: LETTER_PX,
  flyer: LETTER_PX,
  postcard: { w: 888, h: 600 },           // 9.25x6.25in
}

const pageSizeFor = (kind: string) => PAGE_PX[kind] ?? LETTER_PX

/** A real rendering of the piece, scaled to fit. The iframe is sandboxed and inert —
 *  a picture of the page, not a page you can use.
 *
 *  With no `width` it measures the space it is given, so a preview can never be
 *  wider than its column and get its right margin clipped. */
function Paper({ html, kind, width, max = 520, zoom = 1, overlay }: {
  html: string; kind: string; width?: number; max?: number; zoom?: number
  overlay?: React.ReactNode
}) {
  const host = useRef<HTMLDivElement>(null)
  const [measured, setMeasured] = useState(0)

  useEffect(() => {
    if (width) return
    const el = host.current
    if (!el) return
    const fit = () => setMeasured(Math.min(el.clientWidth, max))
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(el)
    return () => ro.disconnect()
  }, [width, max])

  const w = Math.round((width ?? measured) * zoom)
  const size = pageSizeFor(kind)
  const scale = w / size.w
  return (
    <div ref={host} className="paper-host">
      {w ? (
        <div className="mail-paper" style={{ width: w, height: Math.round(size.h * scale) }}>
          <iframe title="Preview" sandbox="" scrolling="no" srcDoc={html}
            style={{ width: size.w, height: size.h, transform: `scale(${scale})` }} />
          {overlay}
        </div>
      ) : null}
    </div>
  )
}

export function MailDesigner() {
  const { data, error, loading, reload } = useAsync(() => api.mailDesigns(), [])
  // What the editor opens on: a piece already saved, a blank layout, or a PDF just
  // dropped in. All three null means we are still choosing.
  const [editing, setEditing] = useState<MailDesign | null>(null)
  const [start, setStart] = useState<MailTemplate | null>(null)
  const [pdf, setPdf] = useState<{ name: string; base64: string } | null>(null)

  if (loading && !data) return <div className="page"><Loading /></div>
  if (error || !data) return <div className="page"><ErrorState error={error} onRetry={reload} /></div>

  if (editing || start || pdf) {
    return <Editor meta={data} design={editing} start={start} startPdf={pdf}
      onClose={() => { setEditing(null); setStart(null); setPdf(null); reload() }}
      onSaved={reload} />
  }
  return <Picker meta={data} onOpen={setEditing} onStart={setStart} onPdf={setPdf}
    onChanged={reload} />
}

// ── Choosing what to send ─────────────────────────────────────────
//
// One page, one list: the pieces already saved and the layouts to start from, with
// whatever is selected rendered at full size beside it. A separate list page for a
// handful of designs was a page you only ever opened in order to leave it.

function Picker({ meta, onOpen, onStart, onPdf, onChanged }: {
  meta: MailDesignsResponse
  onOpen: (d: MailDesign) => void
  onStart: (t: MailTemplate) => void
  onPdf: (f: { name: string; base64: string }) => void
  onChanged: () => void
}) {
  const confirm = useConfirm()
  const toast = useToast()
  const [at, setAt] = useState(0)
  const [preview, setPreview] = useState<MailPreview | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const saved = meta.designs
  const design: MailDesign | null = (at < saved.length ? saved[at] : null) ?? null
  const template: MailTemplate | undefined =
    at < saved.length ? undefined : meta.templates[at - saved.length]

  // The real renderer, so what you pick is what prints. A layout ships its own
  // rendering; a saved piece has to be rendered with the words that are in it.
  useEffect(() => {
    if (!design || design.source === 'upload') return setPreview(null)
    let live = true
    setPreview(null)
    api.previewMailDesign({
      name: design.name, kind: design.kind, source: design.source,
      template_key: design.template_key, layout: design.layout, color: design.color,
      fields: design.fields,
    }).then((p) => { if (live) setPreview(p) }).catch(() => {})
    return () => { live = false }
  }, [design?.id])

  async function archive(d: MailDesign) {
    if (!await confirm({
      title: `Archive “${d.name}”?`,
      body: 'It stops appearing here. Pieces already mailed keep their history.',
      confirmLabel: 'Archive it', danger: true,
    })) return
    try {
      await api.archiveMailDesign(d.id)
      toast('Archived', { tone: 'positive' })
      setAt(0)
      onChanged()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  async function onFile(file: File) {
    const base64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader()
      r.onload = () => resolve(String(r.result).split(',')[1] ?? '')
      r.onerror = () => reject(r.error)
      r.readAsDataURL(file)
    })
    onPdf({ name: file.name, base64 })
  }

  const kind = design?.kind ?? template?.kind ?? 'letter'
  const html = design ? (preview?.pages[0] ?? '') : (template?.preview_html ?? '')

  return (
    <div className="page" style={{ maxWidth: 1240 }}>
      <div className="page-head">
        <div>
          <h1 className="h1">What are you sending?</h1>
          <p className="subline">
            Open a piece you have saved, or start from a layout — you can change every word
            afterwards. Every layout carries the NMLS line, the Equal Housing mark and an
            opt-out notice.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
          <button className="btn" onClick={() => fileRef.current?.click()}>
            <Icon name="upload_file" size={15} /> Upload a PDF
          </button>
        </div>
      </div>

      {!meta.lob.configured ? (
        <div className="notice warning" style={{ marginBottom: 14 }}>
          <Icon name="info" />
          <span>Printing isn’t configured on this server, so proofs and sending are off.
            You can still write and save designs.</span>
        </div>
      ) : null}

      <div className="layout-pick">
        <div className="layout-list">
          {saved.length ? <span className="pick-group">Your pieces</span> : null}
          {saved.map((d, i) => (
            <button key={`d${d.id}`} className={`layout-option${i === at ? ' on' : ''}`}
              onClick={() => setAt(i)}>
              <strong>{d.name}</strong>
              <span className="design-kind">{d.kind}{d.source === 'upload' ? ' · your PDF' : ''}
                {d.lo_id === null ? ' · shared' : ''}</span>
              <p>{d.source === 'upload' ? (d.pdf_name ?? 'Uploaded PDF') : (d.fields.headline || '—')}</p>
            </button>
          ))}
          <span className="pick-group">Start from a layout</span>
          {meta.templates.map((t, i) => (
            <button key={t.key} className={`layout-option${saved.length + i === at ? ' on' : ''}`}
              onClick={() => setAt(saved.length + i)}>
              <strong>{t.name}</strong>
              <span className="design-kind">{t.kind} · {t.size}in</span>
              <p>{t.description}</p>
            </button>
          ))}
          <div className="pick-act">
            {design ? (
              <>
                <button className="btn primary" onClick={() => onOpen(design)}>
                  {design.lo_id === null ? 'Open' : 'Edit this piece'}
                  <Icon name="arrow_forward" size={15} />
                </button>
                {design.lo_id === null ? null : (
                  <button className="iconbtn" aria-label={`Archive ${design.name}`}
                    onClick={() => archive(design)}>
                    <Icon name="archive" size={16} />
                  </button>
                )}
              </>
            ) : template ? (
              <button className="btn primary" onClick={() => onStart(template)}>
                Use this layout <Icon name="arrow_forward" size={15} />
              </button>
            ) : null}
          </div>
        </div>

        {/* The piece, as large as the page allows, on a surface of its own. It is the
            one thing on this screen worth looking at. */}
        <div className="layout-stage immersive">
          <div className="mail-stage">
            {design?.source === 'upload' ? (
              <p className="quiet" style={{ fontSize: 12.5, padding: '60px 0' }}>
                An uploaded PDF prints as it is — open it to take a proof.
              </p>
            ) : html ? (
              <Paper html={html} kind={kind} max={640} />
            ) : <Loading label="Rendering…" />}
            <div className="mail-stage-foot">
              {design ? `Updated ${shortDate(design.updated_at)}`
                : template ? `${template.kind} · ${template.size} inches, printed and mailed by Lob`
                  : ''}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── The editor ──────────────────────────────────────────────────────────────

function Editor({ meta, design, start, startPdf, onClose, onSaved }: {
  meta: MailDesignsResponse
  /** A piece already saved — editing it. */
  design: MailDesign | null
  /** A layout chosen on the way in — a new piece, with its defaults in the boxes. */
  start?: MailTemplate | null
  /** A PDF chosen on the way in — a new piece that is already drawn. */
  startPdf?: { name: string; base64: string } | null
  onClose: () => void
  onSaved: () => void
}) {
  const [templateKey, setTemplateKey] = useState(design?.template_key ?? start?.key ?? '')
  const [name, setName] = useState(
    design?.name ?? start?.name ?? startPdf?.name.replace(/\.pdf$/i, '') ?? '')
  const [fields, setFields] = useState<Record<string, string>>(design?.fields
    ?? (start ? Object.fromEntries(start.blocks.map((b) => [b.key, b.default])) : {}))
  const [pdf, setPdf] = useState<{ name: string; base64: string } | null>(startPdf ?? null)
  const [source, setSource] = useState<'template' | 'upload'>(
    design?.source ?? (startPdf ? 'upload' : 'template'))
  const [layout, setLayout] = useState<string | null>(design?.layout ?? start?.layout ?? null)
  const [color, setColor] = useState<string | null>(design?.color ?? null)
  // The copy is being rewritten; the piece on screen is about to change under you.
  const [drafting, setDrafting] = useState(false)
  // "Direct mail" on a contact or a lead lands here with them in the query, so the
  // send step opens with them already chosen instead of an empty picker.
  const [mailParams] = useSearchParams()
  const forContact = Number(mailParams.get('contact')) || null
  // Warnings the officer has looked at and waved past, by flag. Dismissing one is a
  // decision, not a mistake: the piece still goes to a reviewer, and the full set is
  // one click away under the compliance note.
  const [waived, setWaived] = useState<string[]>([])
  const [preview, setPreview] = useState<MailPreview | null>(null)
  const [previewError, setPreviewError] = useState('')
  const [previewing, setPreviewing] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [face, setFace] = useState(0)
  const [savedId, setSavedId] = useState<number | null>(design?.id ?? null)
  const [busy, setBusy] = useState('')
  const [sending, setSending] = useState(false)
  // One step of undo, so a revision that went the wrong way isn't a retype.
  const [previous, setPrevious] = useState<Record<string, string> | null>(null)
  const toast = useToast()
  const fileRef = useRef<HTMLInputElement>(null)

  const template: MailTemplate | undefined = meta.templates.find((t) => t.key === templateKey)
  const readOnly = design?.lo_id === null
  const kind = source === 'upload' ? (design?.kind ?? 'letter') : (template?.kind ?? 'letter')

  function applyCopy(next: Record<string, string>) {
    setPrevious(fields)
    setFields((f) => ({ ...f, ...next }))
  }

  async function onFile(file: File) {
    const base64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader()
      r.onload = () => resolve(String(r.result).split(',')[1] ?? '')
      r.onerror = () => reject(r.error)
      r.readAsDataURL(file)
    })
    setPdf({ name: file.name, base64 })
    setSource('upload')
    setTemplateKey('')
    if (!name.trim()) setName(file.name.replace(/\.pdf$/i, ''))
    setPreview(null)
  }

  const body = useMemo(() => ({
    name: name.trim() || 'Untitled', source, kind, layout, color,
    template_key: source === 'template' ? templateKey : null, fields,
  }), [name, source, kind, layout, color, templateKey, fields])

  useEffect(() => {
    if (source !== 'template' || !templateKey) return setPreview(null)
    setPreviewing(true)
    const t = window.setTimeout(() => {
      api.previewMailDesign(body).then((p) => { setPreview(p); setPreviewError('') })
        .catch((e) => setPreviewError(errorText(e)))
        .finally(() => setPreviewing(false))
    }, 350)
    return () => window.clearTimeout(t)
  }, [body, source, templateKey])

  async function save(): Promise<number | null> {
    setBusy('save')
    try {
      const payload = { ...body, ...(pdf ? { pdf_base64: pdf.base64, pdf_name: pdf.name } : {}) }
      const saved = savedId
        ? await api.updateMailDesign(savedId, payload)
        : await api.createMailDesign(payload)
      setSavedId(saved.id)
      setPdf(null)
      toast('Saved', { tone: 'positive' })
      return saved.id
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      return null
    } finally {
      setBusy('')
    }
  }

  async function orderProof() {
    const id = savedId ?? await save()
    if (!id) return
    setBusy('proof')
    try {
      const res = await api.proofMailDesign(id)
      if (res.url) window.open(res.url, '_blank', 'noopener')
      toast('A proof was rendered — nothing was printed or charged', { tone: 'positive' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy('')
    }
  }

  const uploadInput = (
    <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
      onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
  )

  return (
    <div className="page mail-designer" style={{ maxWidth: 1180 }}>
      <div className="crumbs">
        <button className="linklike" onClick={onClose}>Mail</button>
        {' › '}{name || 'New design'}
      </div>
      <div className="page-head">
        <div>
          <input className="mail-title" value={name} maxLength={80} disabled={readOnly}
            aria-label="Design name" placeholder="Name this design"
            onChange={(e) => setName(e.target.value)} />
          <p className="subline">
            {readOnly ? 'A shared design — open it to read, or copy the wording into one of your own.'
              : forContact
                ? 'Edit the words or the design, then Send — they are already picked as the recipient. '
                  + 'Proof prints a free test copy at Lob first if you want to see it on paper.'
                : 'Ask for what you want — the words, the design, the colour. The legal footer stays put.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={onClose}>Close</button>
          <button className="btn" disabled={!meta.lob.configured || busy !== ''} onClick={orderProof}>
            <Icon name="print" size={15} /> {busy === 'proof' ? 'Rendering…' : 'Proof'}
          </button>
          <button className="btn" disabled={!meta.lob.configured || busy !== ''}
            onClick={async () => { if (savedId || await save()) setSending(true) }}>
            <Icon name="send" size={15} /> Send
          </button>
          <button className="btn primary" disabled={readOnly || busy !== ''} onClick={() => save().then(() => onSaved())}>
            {busy === 'save' ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* The box you type in sits above the piece it changes — you write, then look
          down at the result, rather than across. */}
      <div className="mail-split">
        <section className="card mail-editor">
          <div className="card-body">
            {source === 'upload' ? (
              <div className="mail-upload">
                <Icon name="picture_as_pdf" size={22} />
                <div>
                  <strong>{pdf?.name ?? design?.pdf_name ?? 'No PDF yet'}</strong>
                  <p>Printed exactly as it is. Check it carries the NMLS number, the Equal Housing
                    mark and a way to opt out — our layouts add those for you, yours can’t.</p>
                </div>
                {uploadInput}
                <button className="btn sm" onClick={() => fileRef.current?.click()}>Replace</button>
              </div>
            ) : meta.ai && !readOnly && template ? (
              <WriteItForMe template={template} fields={fields} layout={layout} color={color}
                onApply={applyCopy} onLayout={setLayout} onColor={setColor} onBusy={setDrafting}
                undo={previous ? () => { setFields(previous); setPrevious(null) } : null} />
            ) : (
              <p className="quiet" style={{ fontSize: 12.5, margin: 0 }}>
                Drafting isn’t set up on this server, so this design can only be edited by
                someone with it configured.
              </p>
            )}
          </div>
        </section>

        <section className="card mail-preview-col">
          <div className="card-head">
            <h2 className="h2">As it prints</h2>
            {drafting || previewing ? (
              <span className="updating"><i /> {drafting ? 'Rewriting…' : 'Updating…'}</span>
            ) : null}
            <span className="spacer" />
            {preview && preview.pages.length > 1 ? (
              <span className="wb-chips" style={{ marginRight: 10 }}>
                <button className={`chip-btn${face === 0 ? ' on' : ''}`} onClick={() => setFace(0)}>Front</button>
                <button className={`chip-btn${face === 1 ? ' on' : ''}`} onClick={() => setFace(1)}>Back</button>
              </span>
            ) : null}
            <span className="zoomer">
              <button className="iconbtn" aria-label="Zoom out" disabled={zoom <= 0.6}
                onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.2).toFixed(2)))}>
                <Icon name="remove" size={16} />
              </button>
              <button className="zoom-level" onClick={() => setZoom(1)} title="Fit the page">
                {Math.round(zoom * 100)}%
              </button>
              <button className="iconbtn" aria-label="Zoom in" disabled={zoom >= 2}
                onClick={() => setZoom((z) => Math.min(2, +(z + 0.2).toFixed(2)))}>
                <Icon name="add" size={16} />
              </button>
            </span>
          </div>
          <div className="card-body" style={{ paddingTop: 0 }}>
            {source === 'upload' ? (
              <p className="quiet" style={{ fontSize: 12.5 }}>
                An uploaded PDF prints as it is — take a proof to see what the printer produces.
              </p>
            ) : previewError ? (
              <div className="notice danger"><Icon name="error" /><span>{previewError}</span></div>
            ) : !preview ? <Loading label="Rendering…" /> : (
              <>
                <div className={`paper-wrap${drafting || previewing ? ' is-busy' : ''}`}>
                  <Paper html={preview.pages[face] ?? preview.pages[0] ?? ''} kind={kind}
                    max={520} zoom={zoom}
                    overlay={/* Flags sit on the page, where the words are — in the
                               masthead's clear zone, which prints blank anyway. */
                      <div className="flag-pins">
                        {preview.warnings.filter((w) => !waived.includes(w.flag)).map((w) => (
                          <Flag key={w.flag} warning={w}
                            onDismiss={() => setWaived((d) => [...d, w.flag])} />
                        ))}
                      </div>} />
                  {drafting || previewing ? <span className="paper-busy" /> : null}
                </div>
                <p className="quiet" style={{ fontSize: 11.5, margin: '10px 0 0', textAlign: 'center' }}>
                  Shown with a sample name — each piece is personalised to the person it goes to.
                </p>
                {/* What prints under the copy either way, and the way back to any
                    flag that was dismissed. */}
                <div className="mail-fine">
                  <strong>Compliance · printed on every piece</strong>
                  <p>{preview.fine_print}</p>
                  {waived.length ? (
                    <button className="btn sm" onClick={() => setWaived([])}>
                      Show {waived.length} dismissed {waived.length === 1 ? 'flag' : 'flags'}
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {sending && savedId ? (
        <SendModal designId={savedId} name={name} liveAvailable={meta.lob.live_available}
          start={forContact ? [{ id: forContact, name: null }] : []}
          onClose={() => setSending(false)} />
      ) : null}
    </div>
  )
}

/** An orange flag on the page: the two-word version, the full note when you want it,
 *  and an × when you have read it and meant what you wrote. */
function Flag({ warning, onDismiss }: { warning: MailWarning; onDismiss: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={`flag-pin${open ? ' open' : ''}`}>
      <div className="flag-pin-head">
        <button className="flag-pin-label" onClick={() => setOpen((v) => !v)}
          aria-expanded={open} title={open ? 'Hide the detail' : warning.note}>
          <Icon name="flag" size={13} />
          <span>{warning.flag}</span>
        </button>
        <button className="flag-pin-x" onClick={onDismiss}
          aria-label={`Dismiss the ${warning.flag} flag`}>
          <Icon name="close" size={13} />
        </button>
      </div>
      {open ? <p>{warning.note}</p> : null}
    </div>
  )
}

// ── Asking for changes ──────────────────────────────────────────────────────
//
// A box you keep talking to, not a one-shot prompt. Every turn carries the wording
// currently on the page, so an edit made by hand is the thing being edited, and the
// reply says which blocks moved.

function WriteItForMe({ template, fields, layout, color, onApply, onLayout, onColor, onBusy, undo }: {
  template: MailTemplate
  fields: Record<string, string>
  layout: string | null
  color: string | null
  onApply: (fields: Record<string, string>) => void
  onLayout: (layout: string) => void
  onColor: (color: string | null) => void
  onBusy: (busy: boolean) => void
  undo: (() => void) | null
}) {
  const [turns, setTurns] = useState<(MailCopyTurn & { changed?: string[] })[]>([])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const endRef = useRef<HTMLDivElement>(null)
  const boxRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }) }, [turns, busy])

  async function send(instruction: string) {
    const said = instruction.trim()
    if (said.length < 3 || busy) return
    setText(''); setError(''); setBusy(true); onBusy(true)
    setTurns((t) => [...t, { role: 'user', content: said }])
    try {
      const res = await api.draftMailCopy({
        instruction: said, template_key: template.key, fields, layout, color,
        history: turns.map(({ role, content }) => ({ role, content })),
      })
      onApply(res.fields)
      if (res.layout_changed) onLayout(res.layout)
      if (res.color_changed) onColor(res.color)
      setTurns((t) => [...t, { role: 'assistant', content: res.note || 'Done.',
                               changed: [...res.changed,
                                         ...(res.layout_changed ? ['__layout'] : []),
                                         ...(res.color_changed ? ['__color'] : [])] }])
      if (res.problem) setError(res.problem)
    } catch (e) {
      setError(errorText(e))
      setTurns((t) => t.slice(0, -1))
      setText(said)
    } finally {
      setBusy(false)
      onBusy(false)
      boxRef.current?.focus()
    }
  }

  const label = (key: string) =>
    key === '__layout' ? 'the design'
      : key === '__color' ? 'the colour'
        : template.blocks.find((b) => b.key === key)?.label ?? key

  return (
    <div className="mail-ask">
      <div className="mail-ask-head">
        <span>Write it for me</span>
        {undo ? (
          <button className="btn sm" onClick={undo} title="Put the wording back as it was">
            <Icon name="undo" size={14} /> Undo
          </button>
        ) : null}
      </div>

      {turns.length ? (
        <div className="copy-thread">
          {turns.map((t, i) => (
            <div key={i} className={`copy-turn ${t.role}`}>
              <div className="copy-bubble">{t.content}</div>
              {t.changed?.length ? <div className="copy-changed">Changed: {t.changed.map(label).join(', ')}</div> : null}
            </div>
          ))}
          {busy ? <div className="copy-turn assistant"><div className="copy-bubble thinking">Writing…</div></div> : null}
          <div ref={endRef} />
        </div>
      ) : null}

      {error ? <div className="notice warning" style={{ margin: '0 0 8px' }}><Icon name="error" /><span>{error}</span></div> : null}

      {/* The two things about the look an officer can change by pointing rather than
          by asking. Everything else about the design is a sentence in the box below. */}
      <div className="look-row">
        {template.layouts.length > 1 ? (
          <span className="look-group">
            <span className="look-label">Design</span>
            {template.layouts.map((l) => (
              <button key={l.key} title={l.description}
                className={`chip-btn${(layout ?? template.layout) === l.key ? ' on' : ''}`}
                onClick={() => onLayout(l.key)}>{l.label}</button>
            ))}
          </span>
        ) : null}
        <span className="look-group">
          <span className="look-label">Colour</span>
          <span className="swatches">
          <button className={`swatch house${!color ? ' on' : ''}`} onClick={() => onColor(null)}
            title="Buckingham brand" aria-label="Brand colour" />
          {Object.entries(template.colors).filter(([n]) => n !== 'magenta').map(([n, hex]) => (
            <button key={n} className={`swatch${color === hex ? ' on' : ''}`} style={{ background: hex }}
            onClick={() => onColor(hex)} title={n} aria-label={n} />
          ))}
          </span>
        </span>
      </div>

      <div className="copy-compose">
        <textarea ref={boxRef} className="field" rows={2} value={text} maxLength={1500} disabled={busy}
          placeholder="Say what to change — “shorter and warmer”, “make it bolder”, “this should read like a personal note” — or paste your own words."
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(text) } }} />
        <button className="btn primary" disabled={busy || text.trim().length < 3}
          aria-label={busy ? 'Writing' : 'Send'} onClick={() => send(text)}>
          {busy ? <span className="btn-spinner" /> : <Icon name="arrow_upward" size={15} />}
        </button>
      </div>
      <p className="quiet" style={{ fontSize: 11, margin: '7px 0 0' }}>
        You can ask for the look as well as the words — “bolder”, “quieter”, “more like a letter”.
        It won’t write a rate, a payment or a promise of approval; those bring disclosure rules with them.
      </p>
    </div>
  )
}

// ── Send it ─────────────────────────────────────────────────────────────────

function SendModal({ designId, name, liveAvailable, onClose, start = [] }: {
  designId: number
  name: string
  liveAvailable: boolean
  onClose: () => void
  /** Already chosen — the contact whose page sent the officer here. */
  start?: { id: number; name: string | null }[]
}) {
  const [people, setPeople] = useState<{ id: number; name: string | null }[]>(start)
  const [live, setLive] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ sent: number; refused: { name: string | null; reason: string }[] } | null>(null)
  const confirm = useConfirm()
  const toast = useToast()

  async function send() {
    if (live && !await confirm({
      title: `Print and mail ${people.length} ${people.length === 1 ? 'piece' : 'pieces'}?`,
      body: `“${name}” goes to the printer and into the post. This costs money and can’t be recalled.`,
      confirmLabel: 'Mail them', danger: true,
    })) return
    setBusy(true)
    try {
      const res = await api.sendMailDesign(designId, people.map((p) => p.id), live)
      setResult({ sent: res.sent.length, refused: res.refused })
      toast(res.live
        ? `${res.sent.length} in the post — about $${(res.cost_cents / 100).toFixed(2)}`
        : `${res.sent.length} created in test mode — nothing printed`, { tone: 'positive' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open width={520} title={`Send “${name}”`} onClose={onClose}
      footer={<><span className="spacer" />
        <button className="btn" onClick={onClose}>{result ? 'Done' : 'Cancel'}</button>
        {!result ? (
          <button className="btn primary" disabled={busy || !people.length} onClick={send}>
            {busy ? 'Sending…' : live ? `Mail ${people.length}` : `Create ${people.length} in test mode`}
          </button>
        ) : null}</>}>
      {result ? (
        <>
          <p style={{ fontSize: 13 }}><strong>{result.sent}</strong> {live ? 'went out' : 'created in test mode'}.</p>
          {result.refused.length ? (
            <>
              <p className="quiet" style={{ fontSize: 12.5 }}>Not sent, and why:</p>
              <ul className="lint-list">
                {result.refused.map((r) => <li key={`${r.name}-${r.reason}`}>{r.name ?? 'Someone'} — {r.reason}</li>)}
              </ul>
            </>
          ) : null}
        </>
      ) : (
        <>
          <PeoplePicker selected={people} onChange={setPeople} placeholder="Search for who gets it" />
          <p className="quiet" style={{ fontSize: 12, marginTop: 10 }}>
            Anyone without a mailing address, in a state we aren’t licensed in, who opted out, or who
            already got this design in the last month is skipped — with the reason on their timeline.
          </p>
          <label className="checkrow" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={live} disabled={!liveAvailable}
              onChange={(e) => setLive(e.target.checked)} />
            <span>Actually print and mail these
              <span className="hint"> — {liveAvailable
                ? 'costs money; leave off to see what would happen'
                : 'live mail isn’t enabled on this server'}</span></span>
          </label>
        </>
      )}
    </Modal>
  )
}
